import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Target, AlertCircle, Brain, Monitor, Wrench, Users } from 'lucide-react';
import { useEventTracking } from '../hooks/useEventTracking';
import { Helmet } from 'react-helmet-async';
import { CONTACT_DETAILS } from '../config/contactDetails';

export const LandingPage: React.FC = () => {
    const { trackPageView, trackClick, isSessionReady } = useEventTracking();
    const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (isSessionReady) {
            trackPageView('/', document.referrer);
        }

        // Animate metrics when they come into view
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const id = entry.target.id;
                    if (id && !visibleSections.has(id)) {
                        setVisibleSections(prev => new Set([...prev, id]));

                        if (id.startsWith('metric')) {
                            setTimeout(() => animateMetric(id), 100);
                        }
                    }
                }
            });
        }, { threshold: 0.5 });

        document.querySelectorAll('.fade-in, [id^="metric"]').forEach((el) => {
            observer.observe(el);
        });

        return () => observer.disconnect();
    }, [isSessionReady]);

    const animateMetric = (elementId: string) => {
        const targets = { metric1: 75, metric2: 25, metric3: 20 };
        const target = targets[elementId as keyof typeof targets];
        if (!target) return;

        const element = document.getElementById(elementId);
        if (!element) return;

        let count = 0;
        const increment = Math.ceil(target / 100);
        const timer = setInterval(() => {
            count += increment;
            if (count >= target) {
                count = target;
                clearInterval(timer);
            }
            element.textContent = count + '%';
        }, 20);
    };

    const handleCTAClick = (ctaType: string) => {
        trackClick(`cta-${ctaType}`, `${ctaType} CTA`, 'button');
    };

    /*const industries = [
        { icon: '🏥', name: 'Healthcare', desc: 'Patient outcome prediction, resource planning' },
        { icon: '🏭', name: 'Manufacturing', desc: 'Quality control, predictive maintenance' },
        { icon: '🌲', name: 'Forestry', desc: 'Growth modeling, sustainable harvesting' },
        { icon: '🏖️', name: 'Tourism', desc: 'Demand forecasting, experience optimization' },
        { icon: '🌾', name: 'Agriculture', desc: 'Yield prediction, resource management' },
        { icon: '⚡', name: 'Energy', desc: 'Load forecasting, renewable integration' }
    ];*/

    return (
        <>
            <Helmet>
                <title>{`${CONTACT_DETAILS.company.name} | AI-Powered Predictive Analytics Across Industries | BC and AB`}</title>
                <meta name="description" content={`Transform data into actionable intelligence across healthcare, manufacturing, agriculture, energy, and more. ${CONTACT_DETAILS.company.name} provides AI-powered predictive analytics with proven four-element methodology.`} />
                <meta name="keywords" content={`${CONTACT_DETAILS.company.name}, predictive analytics, cross-industry AI, data intelligence, four-element methodology, British Columbia, Alberta, forecast labs, forecast`} />
                <link rel="canonical" href="https://4castlabs.com/" />
                <meta property="og:title" content={`Transform Data Into Actionable Intelligence - ${CONTACT_DETAILS.company.name}`} />
                <meta property="og:description" content="AI-powered predictive analytics across industries. From healthcare to manufacturing, agriculture to energy - one proven methodology, multiple applications." />
                <meta property="og:url" content="https://4castlabs.com/" />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://4castlabs.com/images/logo.webp" />
            </Helmet>

            <div className="bg-gray-50 text-gray-800 font-sans leading-relaxed">
                {/* Hero Section */}
                <header className="bg-gradient-to-br from-blue-900 to-indigo-700 text-white py-16">
                    <div className="max-w-6xl mx-auto px-4 text-center">
                        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6">
                            Transform Data Into
                            <span className="block text-yellow-400">Actionable Intelligence</span>
                        </h1>
                        <p className="text-xl md:text-2xl mb-8 opacity-95 max-w-4xl mx-auto">
                            {CONTACT_DETAILS.company.name} applies AI-powered predictive analytics to optimize systems
                            and preserve institutional knowledge - from healthcare to manufacturing, agriculture to
                            energy.
                        </p>

                        {/*<div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8 max-w-4xl mx-auto">
                            {industries.map((industry, idx) => (
                                <div key={idx}
                                     className="bg-white bg-opacity-10 rounded-lg p-4 text-center backdrop-blur-sm">
                                    <div className="text-2xl mb-2">{industry.icon}</div>
                                    <div className="text-sm font-medium">{industry.name}</div>
                                </div>
                            ))}
                        </div>*/}

                        <Link
                            to="/assessment"
                            onClick={() => handleCTAClick('discover-potential')}
                            className="inline-block bg-yellow-500 hover:bg-yellow-400 text-blue-900 font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                        >
                            Discover Your Predictive Potential
                            <ArrowRight className="inline ml-2 h-5 w-5"/>
                        </Link>

                        <p className="text-blue-200 mt-4">Seeking pilot partners across multiple industries</p>
                    </div>
                </header>

                <div className="max-w-6xl mx-auto px-4 py-4">
                    {/* Universal Challenges Section */}
                    <section className="py-16 fade-in">
                        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-blue-800">
                            The Universal Challenge: Reactive Decision-Making
                        </h2>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
                            <div
                                className="bg-white shadow-lg rounded-lg p-6 text-center hover:shadow-xl transition-shadow border-t-4 border-red-500">
                                <div className="text-4xl mb-4">🔥</div>
                                <h3 className="text-xl font-semibold mb-3 text-blue-800">Firefighting Mode</h3>
                                <p className="text-gray-600">Constantly reacting to crises instead of preventing
                                    them</p>
                            </div>

                            <div
                                className="bg-white shadow-lg rounded-lg p-6 text-center hover:shadow-xl transition-shadow border-t-4 border-orange-500">
                                <div className="text-4xl mb-4">🏝️</div>
                                <h3 className="text-xl font-semibold mb-3 text-blue-800">Data Silos</h3>
                                <p className="text-gray-600">Critical information trapped in disconnected systems</p>
                            </div>

                            <div
                                className="bg-white shadow-lg rounded-lg p-6 text-center hover:shadow-xl transition-shadow border-t-4 border-purple-500">
                                <div className="text-4xl mb-4">💸</div>
                                <h3 className="text-xl font-semibold mb-3 text-blue-800">Resource Waste</h3>
                                <p className="text-gray-600">Poor allocation decisions without predictive insights</p>
                            </div>

                            <div
                                className="bg-white shadow-lg rounded-lg p-6 text-center hover:shadow-xl transition-shadow border-t-4 border-teal-500">
                                <div className="text-4xl mb-4">🧠</div>
                                <h3 className="text-xl font-semibold mb-3 text-blue-800">Knowledge Loss</h3>
                                <p className="text-gray-600">Critical expertise leaving with retiring experts</p>
                            </div>
                        </div>
                    </section>

                    {/* Solution: Universal Methodology */}
                    <section className="py-16 bg-gradient-to-r from-green-200 to-indigo-200 -mx-4 px-4 fade-in">
                        <h2 className="text-4xl font-bold text-center mb-12 text-blue-800">
                            One Proven Methodology, Multiple Industries
                        </h2>

                        <div className="max-w-4xl mx-auto relative grid grid-cols-1 sm:grid-cols-2 gap-8 mb-12">

                            <div
                                className="hidden sm:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 z-10 pointer-events-none opacity-70">
                                <img src='/images/logo.webp'
                                      alt={`${CONTACT_DETAILS.company.name} Cycle`}
                                     className="w-full h-full object-contain"
                                     loading="lazy" />
                            </div>

                            <div
                                className="bg-blue-50 rounded-lg p-6 text-center shadow-lg transition-all transform hover:-translate-y-2 border-t-4 border-blue-600">
                                <div
                                    className="bg-blue-600 text-white rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                    <Monitor className="h-8 w-8"/>
                                </div>
                                <h3 className="text-xl font-semibold mb-3 text-blue-800">MONITOR</h3>
                                <p className="text-gray-600">Collect and integrate data from existing systems across any
                                    industry</p>
                            </div>

                            <div
                                className="bg-green-50 rounded-lg p-6 text-center shadow-lg transition-all transform hover:-translate-y-2 border-t-4 border-green-600">
                                <div
                                    className="bg-green-600 text-white rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                    <Brain className="h-8 w-8"/>
                                </div>
                                <h3 className="text-xl font-semibold mb-3 text-blue-800">PREDICT</h3>
                                <p className="text-gray-600">Use AI/ML to identify patterns and forecast outcomes</p>
                            </div>

                            <div
                                className="bg-purple-100 rounded-lg p-6 text-center shadow-lg transition-all transform hover:-translate-y-2 border-t-4 border-purple-600">
                                <div
                                    className="bg-purple-600 text-white rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                    <Target className="h-8 w-8"/>
                                </div>
                                <h3 className="text-xl font-semibold mb-3 text-blue-800">OPTIMIZE</h3>
                                <p className="text-gray-600">Provide actionable recommendations for resource
                                    allocation</p>
                            </div>

                            <div
                                className="bg-red-100 rounded-lg p-6 text-center shadow-lg transition-all transform hover:-translate-y-2 border-t-4 border-orange-600">
                                <div
                                    className="bg-orange-600 text-white rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                    <Shield className="h-8 w-8"/>
                                </div>
                                <h3 className="text-xl font-semibold mb-3 text-blue-800">PRESERVE</h3>
                                <p className="text-gray-600">Capture and systematize critical knowledge and
                                    processes</p>
                            </div>
                        </div>
                    </section>

                    {/* Industry Applications */}
                    <section className="py-16 fade-in">
                        <h2 className="text-3xl font-bold text-center mb-6 text-blue-800">Serving organizations across
                            multiple industries</h2>
                        <div className="flex justify-center items-center mb-2">
                            <div
                                className="flex items-center justify-center">
                                <img
                                    src='/images/4-quadrant.webp'
                                    alt="Serving organizations across healthcare, manufacturing, agriculture, energy, forestry, and tourism"
                                    className="w-full h-80 object-cover rounded-lg shadow-lg mb-2"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                        {/*<h2 className="text-4xl font-bold text-center mb-12 text-blue-800">Cross-Industry*/}
                        {/*    Applications</h2>*/}

                        {/*<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[
                                {
                                    icon: '🏥',
                                    title: 'Healthcare',
                                    applications: ['Patient outcome prediction', 'Resource allocation', 'Early intervention systems'],
                                    bg: 'from-red-50 to-pink-50',
                                    border: 'border-red-200'
                                },
                                {
                                    icon: '🏭',
                                    title: 'Manufacturing',
                                    applications: ['Equipment failure prediction', 'Quality control', 'Supply chain optimization'],
                                    bg: 'from-blue-50 to-cyan-50',
                                    border: 'border-blue-200'
                                },
                                {
                                    icon: '🌲',
                                    title: 'Forestry',
                                    applications: ['Growth modeling', 'Harvest optimization', 'Disease prediction'],
                                    bg: 'from-green-50 to-emerald-50',
                                    border: 'border-green-200'
                                },
                                {
                                    icon: '🏖️',
                                    title: 'Tourism',
                                    applications: ['Demand forecasting', 'Capacity planning', 'Experience optimization'],
                                    bg: 'from-yellow-50 to-amber-50',
                                    border: 'border-yellow-200'
                                },
                                {
                                    icon: '🌾',
                                    title: 'Agriculture',
                                    applications: ['Yield prediction', 'Irrigation optimization', 'Pest management'],
                                    bg: 'from-orange-50 to-yellow-50',
                                    border: 'border-orange-200'
                                },
                                {
                                    icon: '⚡',
                                    title: 'Energy',
                                    applications: ['Load forecasting', 'Renewable integration', 'Grid optimization'],
                                    bg: 'from-purple-50 to-indigo-50',
                                    border: 'border-purple-200'
                                }
                            ].map((industry, idx) => (
                                <div key={idx}
                                     className={`bg-gradient-to-br ${industry.bg} border-2 ${industry.border} rounded-xl p-6 hover:shadow-lg transition-shadow`}>
                                    <div className="text-4xl mb-4">{industry.icon}</div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-4">{industry.title}</h3>
                                    <ul className="space-y-2">
                                        {industry.applications.map((app, appIdx) => (
                                            <li key={appIdx} className="flex items-start">
                                                <CheckCircle
                                                    className="h-4 w-4 text-green-500 mr-2 mt-1 flex-shrink-0"/>
                                                <span className="text-gray-700">{app}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>*/}
                    </section>

                    {/* Results Section */}
                    <section className="py-16 bg-blue-800 text-white -mx-4 px-4 fade-in">
                        <h2 className="text-4xl font-bold text-center mb-8">Proven Results Across Industries</h2>
                        <p className="text-center text-blue-200 mb-12 text-lg">Based on industry studies and
                            implementations</p>

                        <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto text-center">
                            <div>
                                <div id="metric1" className="text-6xl font-bold mb-4 text-yellow-400">0%</div>
                                <p className="text-xl">Reduction in reactive decision-making</p>
                            </div>
                            <div>
                            <div id="metric2" className="text-6xl font-bold mb-4 text-yellow-400">0%</div>
                                <p className="text-xl">Cost savings through optimization</p>
                            </div>
                            <div>
                                <div id="metric3" className="text-6xl font-bold mb-4 text-yellow-400">0%</div>
                                <p className="text-xl">Improvement in resource efficiency</p>
                            </div>
                        </div>
                        <p className="text-center text-sm text-blue-200 mt-8">*Results vary by industry and
                            implementation</p>
                    </section>

                    {/* Pilot Program Section */}
                    <section className="py-16 bg-gradient-to-r from-green-600 to-green-800 text-white -mx-4 px-4">
                        <div className="max-w-4xl mx-auto text-center">
                            <div className="bg-orange-400 bg-opacity-20 rounded-lg p-6 mb-8 border-2 border-orange-300">
                                <AlertCircle className="h-8 w-8 text-orange-200 mx-auto mb-4"/>
                                <h3 className="text-2xl font-bold text-orange-100 mb-2">Currently Seeking Pilot
                                    Partners</h3>
                                <p className="text-orange-100">
                                    Validate our cross-industry methodology with forward-thinking organizations across
                                    different sectors
                                </p>
                            </div>

                            <h2 className="text-3xl font-bold mb-6">Start Small, Scale Smart</h2>
                            <p className="text-xl text-green-100 mb-8">
                                Choose one critical process or system for a collaborative 3-6 month pilot program
                            </p>

                            <div className="grid md:grid-cols-3 gap-6 mb-8">
                                <div className="text-center">
                                    <div
                                        className="bg-green-500 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                                        <span className="text-white font-bold">1</span>
                                    </div>
                                    <h3 className="font-semibold mb-2">Choose One System</h3>
                                    <p className="text-sm text-green-100">Pick your most critical process or decision
                                        point</p>
                                </div>
                                <div className="text-center">
                                    <div
                                        className="bg-green-500 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                                        <span className="text-white font-bold">2</span>
                                    </div>
                                    <h3 className="font-semibold mb-2">3-6 Month Pilot</h3>
                                    <p className="text-sm text-green-100">Full implementation with predictive
                                        insights</p>
                                </div>
                                <div className="text-center">
                                    <div
                                        className="bg-green-500 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                                        <span className="text-white font-bold">3</span>
                                    </div>
                                    <h3 className="font-semibold mb-2">Proven Value</h3>
                                    <p className="text-sm text-green-100">Scale to additional systems or walk away</p>
                                </div>
                            </div>

                            <Link
                                to="/contact?source=pilot-program"
                                onClick={() => handleCTAClick('pilot-program')}
                                className="inline-flex items-center bg-white text-green-700 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-50 transition-colors"
                            >
                                Apply for Pilot Program
                                <ArrowRight className="ml-2 h-5 w-5"/>
                            </Link>
                        </div>
                    </section>

                    {/* Why 4cast Labs */}
                    <section className="py-16 fade-in">
                        <h2 className="text-4xl font-bold text-center mb-12 text-blue-800">Why {CONTACT_DETAILS.company.name}</h2>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            <div className="text-center">
                                <div
                                    className="bg-blue-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                    <Target className="h-8 w-8 text-blue-600"/>
                                </div>
                                <h3 className="text-lg font-semibold mb-3">Industry Agnostic</h3>
                                <p className="text-gray-600">Same proven methodology across diverse sectors</p>
                            </div>

                            <div className="text-center">
                                <div
                                    className="bg-green-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                    <Wrench className="h-8 w-8 text-green-600"/>
                                </div>
                                <h3 className="text-lg font-semibold mb-3">Software Excellence</h3>
                                <p className="text-gray-600">25+ years building mission-critical systems</p>
                            </div>

                            <div className="text-center">
                                <div
                                    className="bg-purple-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                    <Users className="h-8 w-8 text-purple-600"/>
                                </div>
                                <h3 className="text-lg font-semibold mb-3">Partnership Approach</h3>
                                <p className="text-gray-600">Collaborative development with direct access</p>
                            </div>

                            <div className="text-center">
                                <div
                                    className="bg-orange-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                    <Shield className="h-8 w-8 text-orange-600"/>
                                </div>
                                <h3 className="text-lg font-semibold mb-3">No Vendor Lock-in</h3>
                                <p className="text-gray-600">Enhance existing systems, don't replace them</p>
                            </div>
                        </div>
                    </section>

                    {/* CTA Section */}
                    <section className="py-16 bg-white rounded-2xl shadow-xl text-center mb-4">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">
                            Ready to Transform Your Data Into Intelligence?
                        </h2>
                        <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                            Discover how predictive analytics could revolutionize your industry-specific challenges
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <Link
                                to="/assessment"
                                onClick={() => handleCTAClick('final-assessment')}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-colors flex items-center group"
                            >
                                Take Industry Assessment
                                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform"/>
                            </Link>

                            <Link
                                to="/contact"
                                onClick={() => handleCTAClick('final-contact')}
                                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 rounded-xl font-semibold text-lg transition-colors flex items-center group"
                            >
                                Schedule Consultation
                                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform"/>
                            </Link>
                        </div>

                        <p className="text-gray-500 mt-6">
                            Free consultation • Cross-industry expertise • No commitment
                        </p>
                    </section>
                </div>
            </div>
        </>
    );
};